import { useEffect, useState, useSyncExternalStore } from 'react'
import { useSelector } from 'react-redux'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Loader2 } from 'lucide-react'

import ReviewEntryDialog from './ReviewEntryDialog'

import styles from './PendingApprovalsTable.module.scss'

import { isApiError } from '@/api'
import { approveSharedEntry, rejectSharedEntry } from '@/api/dictionary'
import type { AdminSharedDictionaryRow } from '@/api/generated'
import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog'
import DataTable from '@/components/DataTable'
import Input from '@/components/Input'
import Pagination from '@/components/Pagination'
import { useToast } from '@/components/Toast/ToastContext'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { PENDING_SHARED_QUERY, sharedDictionaryQueryPrefix, useSharedDictionary } from '@/hooks/useSharedDictionary'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import { EM_DASH, formatIsoDate, scopeLabel } from '@/pages/app/DictionarySnippets/format'
import { selectCurrentEnterprise } from '@/store/features/user/userSlice'

const PAGE_SIZE = PENDING_SHARED_QUERY.limit

const SEARCH_DEBOUNCE_MS = 300

// Mirrors the server's `Query(max_length=200)`; a longer term is a 422, not a narrower result.
const SEARCH_MAX_LENGTH = 200

const IMPERSONATION_BLOCKED = 'Action unavailable while impersonating another user'

const requesterLabel = (item: AdminSharedDictionaryRow) =>
  item.requester_name || item.requester_email || 'Former member'

const errorMessage = (error: unknown, fallback: string) => (isApiError(error) ? error.detail || fallback : fallback)

const buildColumns = (
  renderActions: (item: AdminSharedDictionaryRow) => JSX.Element
): ColumnDef<AdminSharedDictionaryRow, string>[] => [
  {
    id: 'word',
    header: 'Trigger',
    accessorFn: (item) => item.word,
    enableSorting: false,
  },
  {
    id: 'kind',
    header: 'Type',
    accessorFn: (item) => (item.is_snippet ? 'Snippet' : 'Word'),
    enableSorting: false,
    cell: ({ getValue }) => <span className={styles.kind}>{getValue()}</span>,
  },
  {
    id: 'replacement',
    header: 'Replacement',
    accessorFn: (item) => item.replacement || EM_DASH,
    enableSorting: false,
    cell: ({ getValue }) => <span className={styles.replacement}>{getValue()}</span>,
  },
  {
    id: 'scope',
    header: 'Shared with',
    accessorFn: scopeLabel,
    enableSorting: false,
  },
  {
    id: 'requester',
    header: 'Requested by',
    accessorFn: requesterLabel,
    enableSorting: false,
    cell: ({ row, getValue }) => (
      <span className={styles.requester}>
        {getValue()}
        <span className={styles.requestedAt}>{formatIsoDate(row.original.requested_at)}</span>
      </span>
    ),
  },
  {
    id: 'actions',
    header: '',
    accessorFn: (item) => item.id,
    enableSorting: false,
    cell: ({ row }) => renderActions(row.original),
  },
]

const PendingApprovalsTable = () => {
  const [page, setPage] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [entryToReject, setEntryToReject] = useState<AdminSharedDictionaryRow | null>(null)
  const [entryToReview, setEntryToReview] = useState<AdminSharedDictionaryRow | null>(null)
  const search = useDebouncedValue(searchInput.trim(), SEARCH_DEBOUNCE_MS, () => setPage(0))
  const isImpersonating = useSyncExternalStore(subscribeImpersonation, getImpersonation) !== null
  const enterpriseId = useSelector(selectCurrentEnterprise)?.id ?? ''

  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const query = { ...PENDING_SHARED_QUERY, search, offset: page * PAGE_SIZE }
  const { data, isLoading, isFetching, isError } = useSharedDictionary(query)

  const rows = data?.rows ?? []
  const total = data?.total ?? 0

  const refresh = () => queryClient.invalidateQueries({ queryKey: sharedDictionaryQueryPrefix(enterpriseId) })

  // Whoever emptied this page (us or another admin), land on the last page that still has rows.
  const isPagePastEnd = data !== undefined && !isFetching && page > 0 && rows.length === 0
  useEffect(() => {
    if (isPagePastEnd) setPage(Math.max(0, Math.ceil(total / PAGE_SIZE) - 1))
  }, [isPagePastEnd, total])

  const settle = async (message: string) => {
    setEntryToReject(null)
    setEntryToReview(null)
    updateToast(message, 'success')
    await refresh()
  }

  // 404/409 mean another admin got there first; without a refetch the stale row stays clickable.
  const failed = async (error: unknown, fallback: string) => {
    updateToast(errorMessage(error, fallback), 'error')
    if (isApiError(error) && (error.status === 404 || error.status === 409)) {
      setEntryToReject(null)
      setEntryToReview(null)
      await refresh()
    }
  }

  const approve = useMutation({
    mutationFn: (wordId: string) => approveSharedEntry(wordId),
    onSuccess: () => settle('Shared entry approved.'),
    onError: (error) => failed(error, 'Failed to approve entry'),
  })

  const reject = useMutation({
    mutationFn: (wordId: string) => rejectSharedEntry(wordId),
    onSuccess: () => settle('Shared entry rejected and removed.'),
    onError: (error) => failed(error, 'Failed to reject entry'),
  })

  // Every non-GET is refused during "View as Admin"; say why rather than surfacing a bare 403.
  const guarded = (act: () => void) => () => {
    if (isImpersonating) {
      updateToast(IMPERSONATION_BLOCKED, 'error')
      return
    }
    act()
  }

  const isDeciding = approve.isPending || reject.isPending

  const renderActions = (item: AdminSharedDictionaryRow) => (
    <div className={styles.actions} onClick={(event) => event.stopPropagation()} role="presentation">
      {/* Approve lives only in the dialog: the cell truncates, and an admin must read what they publish. */}
      <Button
        title="Review"
        type="dark"
        isDisabled={isDeciding}
        ariaLabel={`Review ${item.word}`}
        onClick={() => setEntryToReview(item)}
      />
      <Button
        title="Reject"
        type="destructiveLight"
        isDisabled={isDeciding}
        ariaLabel={`Reject ${item.word}`}
        onClick={guarded(() => setEntryToReject(item))}
      />
    </div>
  )

  const columns = buildColumns(renderActions)

  const isStale = isFetching || searchInput.trim() !== search

  return (
    <div className={styles.content}>
      <div className={styles.searchRow}>
        <Input
          type="text"
          value={searchInput}
          onInput={setSearchInput}
          placeholder="Search requests"
          ariaLabel="Search pending shared entries"
          maxLength={SEARCH_MAX_LENGTH}
          className={styles.search}
        />
        {isStale && <Loader2 className={styles.spinner} aria-hidden="true" />}
      </div>
      {isError ? (
        <p className={styles.message}>Could not load pending requests. Refresh to try again.</p>
      ) : (
        <>
          <DataTable
            data={rows}
            columns={columns}
            isLoading={isLoading}
            onRowClick={setEntryToReview}
            manualPagination
            emptyMessage={
              search ? 'No entries match your search.' : 'No dictionary words or snippets are awaiting approval.'
            }
            ariaLabel="Pending dictionary and snippet approvals"
          />
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPrev={() => setPage((current) => Math.max(0, current - 1))}
            onNext={() => setPage((current) => current + 1)}
          />
        </>
      )}
      <ReviewEntryDialog
        entry={entryToReview}
        requester={entryToReview ? requesterLabel(entryToReview) : ''}
        isApproving={approve.isPending}
        onApprove={guarded(() => {
          if (entryToReview) approve.mutate(entryToReview.id)
        })}
        onReject={guarded(() => {
          setEntryToReject(entryToReview)
          setEntryToReview(null)
        })}
        onClose={() => setEntryToReview(null)}
      />
      <ConfirmDialog
        isOpen={entryToReject !== null}
        title={`Reject "${entryToReject?.word ?? ''}"?`}
        message="This removes the request for good — it won't show up here again unless it's submitted again. The person who submitted it won't be notified."
        warning={<strong>This action cannot be undone.</strong>}
        confirmLabel={reject.isPending ? 'Rejecting…' : 'Reject request'}
        isConfirming={reject.isPending}
        onConfirm={guarded(() => {
          if (entryToReject) reject.mutate(entryToReject.id)
        })}
        onCancel={() => setEntryToReject(null)}
      />
    </div>
  )
}

export default PendingApprovalsTable
